import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { patch } from "../../lib/request"
import { buildPostsApiPatchPayload } from "../../lib/utils/postPatchPayload"

function normalizeBulkPostIds(postIds) {
  const raw = Array.isArray(postIds) ? postIds : []
  const numeric = raw
    .map((id) => (typeof id === "number" ? id : Number(id)))
    .filter((n) => n != null && !Number.isNaN(n))
  return [...new Set(numeric)]
}

// Post status constants
export const POST_STATUS = {
  DRAFT: 1,
  SENT_TO_CREATOR: 5,
  SCHEDULED: 6,
  SENT_FOR_EXTERNAL_APPROVAL: 8,
}

// Async thunk for updating post status
export const updatePostStatus = createAsyncThunk(
  "postStatus/updateStatus",
  async ({ postId, contentPostStatusId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!postId) {
        throw new Error("Post ID is required")
      }

      if (!contentPostStatusId) {
        throw new Error("Content post status ID is required")
      }

      const response = await patch(
        `posts/${postId}`,
        buildPostsApiPatchPayload({ contentPostStatusId }),
      )

      return { response, postId, contentPostStatusId }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update post status")
    }
  },
)

/** PATCH /posts/bulk/status — bulk status changes (review bulk actions, etc.). */
export const updatePostsBulkStatus = createAsyncThunk(
  "postStatus/updateBulkStatus",
  async ({ postIds, contentPostStatusId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const unique = normalizeBulkPostIds(postIds)
      if (unique.length === 0) {
        throw new Error("At least one post ID is required")
      }

      if (!contentPostStatusId) {
        throw new Error("Content post status ID is required")
      }

      const response = await patch("posts/bulk/status", {
        postIds: unique,
        contentPostStatusId,
      })

      return { response, postIds: unique, contentPostStatusId }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update post statuses")
    }
  },
)

const postStatusSlice = createSlice({
  name: "postStatus",
  initialState: {
    updateStatus: "idle", // idle | loading | succeeded | failed
    updateError: null,
    lastUpdatedPostId: null,
  },
  reducers: {
    clearUpdateError: (state) => {
      state.updateError = null
    },
    resetUpdateStatus: (state) => {
      state.updateStatus = "idle"
      state.updateError = null
      state.lastUpdatedPostId = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updatePostStatus.pending, (state) => {
        state.updateStatus = "loading"
        state.updateError = null
      })
      .addCase(updatePostStatus.fulfilled, (state, action) => {
        state.updateStatus = "succeeded"
        state.updateError = null
        state.lastUpdatedPostId = action.payload.postId
      })
      .addCase(updatePostStatus.rejected, (state, action) => {
        state.updateStatus = "failed"
        state.updateError = action.payload
      })
      .addCase(updatePostsBulkStatus.rejected, (state, action) => {
        state.updateError = action.payload
      })
  },
})

export const { clearUpdateError, resetUpdateStatus } = postStatusSlice.actions
export default postStatusSlice.reducer
